import { useState, useEffect, useRef } from 'react';
import { useApp } from '../context/AppContext';
import { Modal, ModalHeader, ModalBody, ModalFooter } from '../components/Modal';
import CameraCapture from '../components/CameraCapture';
import DateRangePicker from '../components/DateRangePicker';
import { calcRental, resolveRate } from '../utils/calcRental';
import { buildContractContext } from '../utils/contractContext';
import { renderContract, printContract } from '../utils/renderContract';
import ContractDocument from '../components/ContractDocument';

const STEPS = ['Asset & Customer', 'Dates & Pricing', 'Photos & Condition', 'Agreement & Sign'];

const COND_OPTIONS = [
  { value: 'no_damage',     label: '✅ No damage — clean' },
  { value: 'small_scratch', label: '🔸 Small scratch / scuff' },
  { value: 'damage',        label: '⚠️ Visible damage' },
  { value: 'major_damage',  label: '🔴 Major damage / accident' },
];

const STATUS_CLS = { active: 's-ac', overdue: 's-ov', available: 's-av', booked: 's-bk', maintenance: 's-mt' };

// Named condition-photo slots (same set as check-in for app-wide consistency)
const PHOTO_SLOTS = ['Front', 'Rear', 'Left side', 'Right side', 'Dashboard', 'Interior'];


function applyRateRules(rawBase, days, startDt, vehicle, rateRules) {
  const applicableRules = (rateRules || []).filter(r => {
    if (!r.active) return false;
    const appliesToV = r.appliesTo === 'all' || (vehicle && r.appliesTo === vehicle.groupId);
    if (!appliesToV) return false;
    const d1 = startDt ? new Date(startDt) : new Date();
    if (r.type === 'duration')  return days >= (r.minDays || 1);
    if (r.type === 'daterange') return d1 >= new Date(r.dateStart) && d1 <= new Date(r.dateEnd);
    if (r.type === 'both')      return days >= (r.minDays || 1) && d1 >= new Date(r.dateStart) && d1 <= new Date(r.dateEnd);
    return false;
  });
  let ruleDiscount = 0, ruleSurcharge = 0;
  applicableRules.forEach(r => {
    const amt = r.valueType === 'percent' ? rawBase * r.value / 100 : r.value * days;
    if (r.effect === 'discount') ruleDiscount += amt;
    else                         ruleSurcharge += amt;
  });
  return { ruleDiscount, ruleSurcharge, applicableRules };
}

export default function AddRentalModal() {
  const { modals, closeModal, openModal, vehicles, customers, addRental, nextContractId, nextInvoiceNum, showToast, rentalPreset, setRentalPreset, setRentalDraft, groups, rateRules, companySettings, maintHist, locations, vehicleModels, agreementTemplates, resolveAgreementTemplate, getDamagesForRental } = useApp();
  const open   = modals['add-rental'];
  const preVid = modals['add-rental-vid'] || null;

  const [step, setStep]               = useState(1);
  const [selectedVid, setSelectedVid] = useState(null);
  const [custName, setCustName]       = useState('');
  const [newCust, setNewCust]         = useState({ fname:'', lname:'', phone:'', email:'', passport:'', licence:'', dob:'', nationality:'', address:'' });
  const [startDt, setStartDt]         = useState('2026-03-16T09:00');
  const [endDt, setEndDt]             = useState('2026-03-23T09:00');
  const [priceType, setPriceType]     = useState('day');
  const [rate, setRate]               = useState('');
  const [discount, setDiscount]       = useState('0');
  const [discType, setDiscType]       = useState('eur');
  const [deposit, setDeposit]         = useState('500');
  const [depositMethod, setDepositMethod] = useState('Card hold (Stripe)');
  const [payMethod, setPayMethod]     = useState('stripe');
  const [cond, setCond]               = useState('no_damage');
  const [condNote, setCondNote]       = useState('');
  const [contractId, setContractId]   = useState('');                // stable id, so a damage report can link before save
  const [notes, setNotes]             = useState('');
  const [agree, setAgree]             = useState('');                // agreement template id
  const [signed, setSigned]           = useState(false);
  const [signatureDataUrl, setSignatureDataUrl] = useState('');      // base64 PNG of canvas
  const [photos, setPhotos]           = useState({});                // { [slotLabel]: base64 dataURL }
  const [activeSlot, setActiveSlot]   = useState(null);              // which slot the file picker fills
  const [camera, setCamera]           = useState(null);              // { label, setPhoto } when camera open
  const [showFullAgreement, setShowFullAgreement] = useState(false); // toggle full contract preview on step 4
  const [pickupLocId, setPickupLocId] = useState('');
  const [returnLocId, setReturnLocId] = useState('');
  const [diffReturn, setDiffReturn]   = useState(false);
  const [vehSearch, setVehSearch]     = useState('');
  const [vehStatus, setVehStatus]     = useState('all');
  const [vehPage, setVehPage]         = useState(1);
  const VEH_PER_PAGE = 10;

  const canvasRef  = useRef(null);
  const drawingRef = useRef(false);
  const lastPosRef = useRef(null);
  const photoInputRef = useRef(null);

  const vehicle          = vehicles.find(v => v.id === selectedVid);
  const selectedCustomer = customers.find(c => `${c.fname} ${c.lname}`.trim() === custName);
  const isNewCust        = custName === '__new__';

  useEffect(() => {
    if (open) {
      setStep(1);
      if (rentalPreset) {
        setSelectedVid(rentalPreset.vid || preVid || null);
        setStartDt(rentalPreset.start || '2026-03-16T09:00');
        setEndDt(rentalPreset.end || '2026-03-23T09:00');
        setRentalPreset(null);
      } else {
        setSelectedVid(preVid || null);
      }
      setCustName('');
      setSigned(false);
      setSignatureDataUrl('');
      setPhotos({});
      setActiveSlot(null);
      setCamera(null);
      setCond('no_damage');
      setCondNote('');
      setContractId(nextContractId());   // reserve the id this rental will use
      setNotes('');
      // Default agreement template = first rental-applicable template
      const defaultTmpl = (agreementTemplates || []).find(t => t.isDefault && (t.appliesTo === 'rental' || t.appliesTo === 'both' || !t.appliesTo))
        || (agreementTemplates || []).find(t => t.appliesTo === 'rental' || t.appliesTo === 'both' || !t.appliesTo)
        || (agreementTemplates || [])[0];
      setAgree(defaultTmpl?.id || '');
      setPickupLocId((locations && locations[0]?.id) || '');
      setReturnLocId('');
      setDiffReturn(false);
      setVehSearch('');
      setVehStatus('all');
      setVehPage(1);
    }
  }, [open]);

  useEffect(() => {
    if (vehicle) {
      const effectiveRate = resolveRate(vehicle, groups, vehicleModels);
      setRate(effectiveRate.toString());
      setDeposit((vehicle.deposit ?? companySettings?.defaultDeposit ?? 500).toString());
    }
  }, [selectedVid]);

  const days    = Math.max(1, Math.round((new Date(endDt) - new Date(startDt)) / 86400000));
  const vatRate = parseFloat(companySettings?.vatRate || 19) / 100;
  const vatMode = companySettings?.vatMode || 'inclusive';

  // Rate rules (AddRental-specific: long-stay discounts / peak surcharges)
  const rawBase = (parseFloat(rate) || 0) * days;
  const { ruleDiscount, ruleSurcharge, applicableRules } = applyRateRules(rawBase, days, startDt, vehicle, rateRules);
  const adjustedRate = Math.max(0, (parseFloat(rate) || 0) - ruleDiscount / days + ruleSurcharge / days);

  const calc = calcRental({
    rate:         adjustedRate,
    days,
    discount:     parseFloat(discount) || 0,
    discType,
    selectedIns:  null,
    extrasConfig: [],
    selectedExtras: {},
    locFee:       0,
    vatMode,
    vatRate,
    vehicleName:  vehicle?.name || 'Vehicle',
  });

  const sub   = calc.baseSub;
  const vat   = calc.vatAmt;
  const disc  = calc.discAmt;
  const total = calc.grandTotal;

  // Signature canvas (works on mouse + touch)
  function canvasPos(e) {
    const rect = canvasRef.current.getBoundingClientRect();
    const scaleX = canvasRef.current.width / rect.width;
    const scaleY = canvasRef.current.height / rect.height;
    const point = e.touches ? e.touches[0] : e;
    return { x: (point.clientX - rect.left) * scaleX, y: (point.clientY - rect.top) * scaleY };
  }
  function canvasDown(e) {
    e.preventDefault();
    drawingRef.current = true;
    lastPosRef.current = canvasPos(e);
  }
  function canvasMove(e) {
    if (!drawingRef.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const pos = canvasPos(e);
    ctx.beginPath();
    ctx.moveTo(lastPosRef.current.x, lastPosRef.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.strokeStyle = '#1A3A5C';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.stroke();
    lastPosRef.current = pos;
  }
  function canvasUp() { drawingRef.current = false; }
  function clearCanvas() {
    const ctx = canvasRef.current.getContext('2d');
    ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    setSigned(false);
    setSignatureDataUrl('');
  }
  function confirmSignature() {
    // Verify the canvas has ink — at least one non-transparent pixel
    const ctx = canvasRef.current.getContext('2d');
    const px = ctx.getImageData(0, 0, canvasRef.current.width, canvasRef.current.height).data;
    const hasInk = px.some((v, i) => i % 4 === 3 && v > 10);
    if (!hasInk) { showToast('Please draw your signature first'); return; }
    setSignatureDataUrl(canvasRef.current.toDataURL('image/png'));
    setSigned(true);
  }

  // Resolve the agreement template + filled context for the current form state
  // (shared by the on-screen View and the Print button on step 4).
  function getRentalContract() {
    const customer = isNewCust ? `${newCust.fname} ${newCust.lname}`.trim() : custName;
    const tmpl = (agreementTemplates || []).find(t => t.id === agree)
      || (resolveAgreementTemplate ? resolveAgreementTemplate(selectedVid, 'rental') : null)
      || (agreementTemplates || []).find(t => t.appliesTo === 'rental' || t.appliesTo === 'both')
      || (agreementTemplates || []).find(t => t.isDefault)
      || (agreementTemplates || [])[0];
    const ctx = buildContractContext({
      rental: {
        id: contractId, customer,
        passport: isNewCust ? newCust.passport : selectedCustomer?.passport,
        licence:  isNewCust ? newCust.licence  : '',
        email:    isNewCust ? newCust.email    : selectedCustomer?.email,
        phone:    isNewCust ? newCust.phone    : selectedCustomer?.phone,
        dob:      isNewCust ? newCust.dob      : selectedCustomer?.dob,
        nationality: isNewCust ? newCust.nationality : selectedCustomer?.nationality,
        address:  isNewCust ? newCust.address  : selectedCustomer?.address,
        startLabel: new Date(startDt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }),
        endLabel:   new Date(endDt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }),
        t1: startDt?.slice(11, 16) || '', t2: endDt?.slice(11, 16) || '',
        pickupLocName: (locations || []).find(l => l.id === pickupLocId)?.name,
        returnLocName: (locations || []).find(l => l.id === (diffReturn ? returnLocId : pickupLocId))?.name,
        calSpan: days,
        rate: parseFloat(rate) || 0,
        deposit: parseFloat(deposit) || 0,
        depositMethod,
        agreedPrice: parseFloat(sub.toFixed(2)),
      },
      vehicle,
      company: companySettings,
      signatureDataUrl: signatureDataUrl || '',
      signedAt: signatureDataUrl ? new Date().toISOString() : '',
    });
    return { tmpl, ctx };
  }

  // Named-slot condition photos (one image per slot, like check-in)
  function openPhotoSlot(label) {
    setActiveSlot(label);
    photoInputRef.current?.click();
  }
  function onSlotFile(e) {
    const file = e.target.files?.[0];
    if (!file || !activeSlot) { e.target.value = ''; return; }
    const reader = new FileReader();
    reader.onload = ev => setPhotos(prev => ({ ...prev, [activeSlot]: ev.target.result }));
    reader.readAsDataURL(file);
    e.target.value = '';
  }
  function openSlotCamera(label) {
    setCamera({
      label: `${label} — Pickup`,
      setPhoto: (data) => setPhotos(prev => ({ ...prev, [label]: data })),
    });
  }
  function removeSlotPhoto(label) {
    setPhotos(prev => { const n = { ...prev }; delete n[label]; return n; });
  }

  function buildRentalData(status) {
    const customer = isNewCust ? `${newCust.fname} ${newCust.lname}`.trim() : custName;
    const id = contractId || nextContractId();
    return {
      id, vid: selectedVid, status, customer,
      phone:       isNewCust ? newCust.phone       : selectedCustomer?.phone       || '',
      email:       isNewCust ? newCust.email        : selectedCustomer?.email       || '',
      passport:    isNewCust ? newCust.passport     : selectedCustomer?.passport    || '',
      licence:     isNewCust ? newCust.licence      : '',
      dob:         isNewCust ? newCust.dob          : selectedCustomer?.dob         || '',
      nationality: isNewCust ? newCust.nationality  : selectedCustomer?.nationality || '',
      address:     isNewCust ? newCust.address      : selectedCustomer?.address     || '',
      start: startDt, end: endDt,
      t1: startDt?.slice(11, 16) || '',
      t2: endDt?.slice(11, 16)   || '',
      pickupLocId: pickupLocId || null,
      pickupLocName: (locations || []).find(l => l.id === pickupLocId)?.name || '',
      returnLocId: (diffReturn ? returnLocId : pickupLocId) || null,
      returnLocName: (locations || []).find(l => l.id === (diffReturn ? returnLocId : pickupLocId))?.name || '',
      startLabel: new Date(startDt).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'}),
      endLabel:   new Date(endDt).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'}),
      priceType, rate: parseFloat(rate) || 0,
      discount: parseFloat(discount) || 0, discType,
      agreedPrice: parseFloat(sub.toFixed(2)),
      deposit: parseFloat(deposit) || 0,
      depositMethod, payMethod,
      condPre: cond, condPreNote: condNote,
      condRet: null, condRetNote: '',
      photosPre: PHOTO_SLOTS.filter(l => photos[l]).map(l => photos[l]), photosRet: [],
      notes,
      agreement: agree || 'standard',
      agreementId: agree || null,
      signed,
      signatureDataUrl: signatureDataUrl || null,
      signData: signatureDataUrl || null,
      signedAt: signatureDataUrl ? new Date().toISOString() : null,
      agreementSaved: signatureDataUrl ? (() => {
        const tmpl = (agreementTemplates || []).find(t => t.id === agree)
          || (resolveAgreementTemplate ? resolveAgreementTemplate(selectedVid, 'rental') : null)
          || (agreementTemplates || []).find(t => t.appliesTo === 'rental' || t.appliesTo === 'both')
          || (agreementTemplates || []).find(t => t.isDefault)
          || (agreementTemplates || [])[0];
        const ctxSnap = buildContractContext({
          rental: {
            id, customer,
            passport: isNewCust ? newCust.passport : selectedCustomer?.passport,
            licence: isNewCust ? newCust.licence : '',
            email: isNewCust ? newCust.email : selectedCustomer?.email,
            phone: isNewCust ? newCust.phone : selectedCustomer?.phone,
            dob: isNewCust ? newCust.dob : selectedCustomer?.dob,
            nationality: isNewCust ? newCust.nationality : selectedCustomer?.nationality,
            address: isNewCust ? newCust.address : selectedCustomer?.address,
            startLabel: new Date(startDt).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'}),
            endLabel:   new Date(endDt).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'}),
            t1: startDt?.slice(11, 16) || '', t2: endDt?.slice(11, 16) || '',
            pickupLocName: (locations || []).find(l => l.id === pickupLocId)?.name,
            returnLocName: (locations || []).find(l => l.id === (diffReturn ? returnLocId : pickupLocId))?.name,
            calSpan: days,
            rate: parseFloat(rate) || 0,
            deposit: parseFloat(deposit) || 0,
            depositMethod,
            agreedPrice: parseFloat(sub.toFixed(2)),
          },
          vehicle,
          company: companySettings,
          signatureDataUrl,
          signedAt: new Date().toISOString(),
        });
        return {
          templateId: tmpl?.id || null,
          templateName: tmpl?.name || 'Rental Agreement',
          body: tmpl?.body || '',
          renderedHtml: tmpl ? renderContract(tmpl, ctxSnap) : '',
          context: ctxSnap,
          signedAt: new Date().toISOString(),
          signedAtFormatted: new Date().toLocaleString('en-GB'),
          customerName: customer,
          vehicleName: vehicle?.name || '',
          vehiclePlate: vehicle?.plate || '',
          signData: signatureDataUrl,
        };
      })() : null,
      invoiceId: nextInvoiceNum(),
      calStart: new Date(startDt).getDate(),
      calSpan: days,
      createdAt: new Date().toISOString().slice(0,10),
    };
  }

  function submit(asBooking = false) {
    const customer = isNewCust ? `${newCust.fname} ${newCust.lname}`.trim() : custName;
    if (!selectedVid) { showToast('Please select a vehicle'); return; }
    if (!customer)    { showToast('Please select or enter a customer'); return; }

    if (asBooking) {
      // Save as future booking — no check-in needed
      const data = buildRentalData('booked');
      addRental(data);
      closeModal('add-rental');
      showToast(`📅 Booking ${data.id} saved for ${customer}`);
    } else {
      // Start rental — open check-in flow
      const data = buildRentalData('active');
      setRentalDraft(data);
      closeModal('add-rental');
      openModal('check-in');
    }
  }

  if (!open) return null;

  return (
    <>
    <Modal open={!!open} size="lg" onClose={() => closeModal('add-rental')}>
      <ModalHeader title="New Rental" subtitle="Fill in all details to create a rental contract" onClose={() => closeModal('add-rental')} />

      {/* Step tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid var(--bd)' }}>
        {STEPS.map((s, i) => (
          <div key={i} onClick={() => i + 1 < step && setStep(i + 1)} style={{
            flex: 1, padding: '9px 4px', textAlign: 'center', fontSize: 10, fontWeight: 500,
            borderBottom: `2px solid ${step === i + 1 ? 'var(--ac)' : step > i + 1 ? 'var(--gr)' : 'transparent'}`,
            color: step === i + 1 ? 'var(--ac)' : step > i + 1 ? 'var(--gr)' : 'var(--tx3)',
            cursor: i + 1 < step ? 'pointer' : 'default',
          }}>
            {i + 1}. {s}
          </div>
        ))}
      </div>

      <ModalBody>

        {/* ── STEP 1: Asset & Customer ── */}
        {step === 1 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

            {/* Vehicle grid */}
            <div>
              <div className="sdiv" style={{ marginBottom: 10 }}>Select Vehicle</div>

              {/* Search + status filter */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 160px', gap: 8, marginBottom: 10 }}>
                <input
                  type="text"
                  className="fi"
                  placeholder="🔍 Search by name or plate…"
                  value={vehSearch}
                  onChange={e => { setVehSearch(e.target.value); setVehPage(1); }}
                />
                <select
                  className="fi"
                  value={vehStatus}
                  onChange={e => { setVehStatus(e.target.value); setVehPage(1); }}
                >
                  <option value="all">All statuses</option>
                  <option value="available">Available</option>
                  <option value="active">Active</option>
                  <option value="booked">Booked</option>
                  <option value="overdue">Overdue</option>
                  <option value="maintenance">Maintenance</option>
                </select>
              </div>

              {(() => {
                const q = vehSearch.trim().toLowerCase();
                const filtered = vehicles.filter(v => {
                  if (vehStatus !== 'all' && v.status !== vehStatus) return false;
                  if (!q) return true;
                  return (v.name || '').toLowerCase().includes(q)
                      || (v.plate || '').toLowerCase().includes(q);
                });
                const totalPages = Math.max(1, Math.ceil(filtered.length / VEH_PER_PAGE));
                const safePage = Math.min(vehPage, totalPages);
                const start = (safePage - 1) * VEH_PER_PAGE;
                const visible = filtered.slice(start, start + VEH_PER_PAGE);

                return (
                  <>
                    {filtered.length === 0 && (
                      <div style={{ padding: '20px', textAlign: 'center', color: 'var(--tx3)', fontSize: 12, border: '1px dashed var(--bd)', borderRadius: 'var(--r)' }}>
                        No vehicles match your filters.
                      </div>
                    )}
                    {filtered.length > 0 && (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(130px,1fr))', gap: 8 }}>
                        {visible.map(v => {
                          const inMaint = maintHist.some(m => m.vid === v.id && m.status === 'in_progress');
                          return (
                          <div key={v.id} onClick={() => !inMaint && setSelectedVid(v.id)} style={{
                            border: `1.5px solid ${selectedVid === v.id ? 'var(--gr)' : 'var(--bd)'}`,
                            borderRadius: 9, overflow: 'hidden', cursor: inMaint ? 'not-allowed' : 'pointer', transition: 'all .12s',
                            background: 'var(--sur)',
                            opacity: inMaint ? 0.5 : 1,
                            boxShadow: selectedVid === v.id ? '0 0 0 3px rgba(26,122,74,.12)' : 'none',
                          }}>
                            <div style={{ height: 60, background: 'var(--sur3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, position: 'relative', borderBottom: '1px solid var(--bd)' }}>
                              {v.icon}
                              {selectedVid === v.id && (
                                <div style={{ position: 'absolute', top: 4, right: 4, width: 16, height: 16, borderRadius: '50%', background: 'var(--gr)', color: '#fff', fontSize: 9, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>✓</div>
                              )}
                            </div>
                            <div style={{ padding: '6px 8px' }}>
                              <div style={{ fontSize: 10, fontWeight: 500, lineHeight: 1.3 }}>{v.name.split('–')[0].trim()}</div>
                              <div style={{ fontSize: 9, color: 'var(--tx3)', fontFamily: "'DM Mono',monospace" }}>{v.plate}</div>
                              <div style={{ marginTop: 4 }}>
                                <span className={`sp ${STATUS_CLS[v.status] || 's-av'}`} style={{ fontSize: 8 }}>{v.status}</span>
                              </div>
                              {(resolveRate(v, groups, vehicleModels) > 0) && (
                                <div style={{ fontSize: 10, fontWeight: 600, color: v.useGroupPricing && v.groupId ? 'var(--am)' : 'var(--bl)', marginTop: 3, fontFamily: "'DM Mono',monospace" }}>
                                  €{resolveRate(v, groups, vehicleModels)}/day{v.useGroupPricing && v.groupId ? ' 📁' : ''}
                                </div>
                              )}
                            </div>
                          </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Pagination footer */}
                    {filtered.length > VEH_PER_PAGE && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10, fontSize: 11, color: 'var(--tx3)' }}>
                        <div>
                          Showing <strong style={{ color: 'var(--tx)' }}>{start + 1}–{Math.min(start + VEH_PER_PAGE, filtered.length)}</strong> of <strong style={{ color: 'var(--tx)' }}>{filtered.length}</strong>
                        </div>
                        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                          <button
                            type="button"
                            className="btn btn-ghost btn-sm"
                            disabled={safePage <= 1}
                            onClick={() => setVehPage(p => Math.max(1, p - 1))}
                            style={{ opacity: safePage <= 1 ? 0.4 : 1 }}
                          >
                            ← Prev
                          </button>
                          <span style={{ padding: '0 8px', fontWeight: 600, color: 'var(--tx)' }}>
                            {safePage} / {totalPages}
                          </span>
                          <button
                            type="button"
                            className="btn btn-ghost btn-sm"
                            disabled={safePage >= totalPages}
                            onClick={() => setVehPage(p => Math.min(totalPages, p + 1))}
                            style={{ opacity: safePage >= totalPages ? 0.4 : 1 }}
                          >
                            Next →
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>

            {/* Customer */}
            <div>
              <div className="sdiv" style={{ marginBottom: 10 }}>Customer</div>
              <select className="fsel2" value={custName} onChange={e => setCustName(e.target.value)} style={{ width: '100%', marginBottom: 8 }}>
                <option value="">— Select customer —</option>
                {customers.map(c => (
                  <option key={c.id} value={`${c.fname} ${c.lname}`.trim()}>{c.fname} {c.lname}</option>
                ))}
                <option value="__new__">+ New customer...</option>
              </select>

              {custName && !isNewCust && selectedCustomer && (
                <div style={{ background: 'var(--bl-l)', border: '1px solid var(--bl-b)', borderRadius: 'var(--r)', padding: '10px 12px' }}>
                  <div style={{ fontSize: 12, fontWeight: 600 }}>{selectedCustomer.fname} {selectedCustomer.lname}</div>
                  <div style={{ fontSize: 11, color: 'var(--tx2)', marginTop: 2 }}>
                    {[selectedCustomer.phone, selectedCustomer.email].filter(Boolean).join(' · ')}
                  </div>
                </div>
              )}

              {isNewCust && (
                <div style={{ background: 'var(--sur2)', border: '1px solid var(--bd)', borderRadius: 'var(--r)', padding: 12 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--tx2)', marginBottom: 10 }}>New Customer Details</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    {[
                      ['fname','First Name *','text'],['lname','Last Name *','text'],
                      ['email','Email *','email'],['phone','Phone *','tel'],
                      ['passport','ID / Passport No. *','text'],['licence','Driving Licence','text'],
                      ['dob','Date of Birth','date'],['nationality','Nationality','text'],
                    ].map(([k, lbl, type]) => (
                      <div key={k} className="fg">
                        <label className="fl">{lbl}</label>
                        <input type={type} className="fi" value={newCust[k]} onChange={e => setNewCust(p => ({ ...p, [k]: e.target.value }))} />
                      </div>
                    ))}
                    <div className="fg" style={{ gridColumn: '1/-1' }}>
                      <label className="fl">Address</label>
                      <input type="text" className="fi" value={newCust.address} onChange={e => setNewCust(p => ({ ...p, address: e.target.value }))} placeholder="123 Apostolou Pavlou, Paphos" />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Deposit & Payment */}
            <div>
              <div className="sdiv" style={{ marginBottom: 10 }}>Deposit & Payment</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
                <div className="fg">
                  <label className="fl">Deposit (€)</label>
                  <input type="number" className="fi" value={deposit} onChange={e => setDeposit(e.target.value)} />
                </div>
                <div className="fg">
                  <label className="fl">Deposit Method</label>
                  <select className="fsel2" value={depositMethod} onChange={e => setDepositMethod(e.target.value)}>
                    <option>Card hold (Stripe)</option>
                    <option>Cash</option>
                    <option>Bank transfer</option>
                    <option>No deposit</option>
                  </select>
                </div>
              </div>
              <div className="fg">
                <label className="fl">Payment Method</label>
                <select className="fsel2" value={payMethod} onChange={e => setPayMethod(e.target.value)}>
                  <option value="stripe">Stripe · Card</option>
                  <option value="cash">Cash</option>
                  <option value="bank">Bank transfer</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {/* ── STEP 2: Dates & Pricing ── */}
        {step === 2 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div className="sdiv" style={{ marginBottom: 8 }}>Rental Dates</div>
            <DateRangePicker
              startDate={startDt ? startDt.slice(0, 10) : null}
              endDate={endDt ? endDt.slice(0, 10) : null}
              onChange={(d1, d2) => {
                if (d1) setStartDt(d1 + 'T' + (startDt ? startDt.slice(11, 16) : '09:00'));
                if (d2) setEndDt(d2   + 'T' + (endDt   ? endDt.slice(11, 16)   : '09:00'));
              }}
              labelStart="Rental Start"
              labelEnd="Expected Return"
            />
            {/* Time pickers */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 8 }}>
              <div className="fg">
                <label className="fl">Start Time</label>
                <input type="time" className="fi" value={startDt ? startDt.slice(11, 16) : '09:00'}
                  onChange={e => setStartDt((startDt ? startDt.slice(0,10) : new Date().toISOString().slice(0,10)) + 'T' + e.target.value)} />
              </div>
              <div className="fg">
                <label className="fl">Return Time</label>
                <input type="time" className="fi" value={endDt ? endDt.slice(11, 16) : '09:00'}
                  onChange={e => setEndDt((endDt ? endDt.slice(0,10) : new Date().toISOString().slice(0,10)) + 'T' + e.target.value)} />
              </div>
            </div>

            {/* ── Pickup / Return locations ── */}
            {(() => {
              const fmtFee = (l) => l.fee === 0 ? 'Free' : l.fee == null ? 'On request' : '€' + l.fee + ' fee';
              return (
                <>
                  <div className="fg">
                    <label className="fl">Pickup Location</label>
                    <select
                      className="fi"
                      value={pickupLocId || ''}
                      onChange={e => setPickupLocId(e.target.value)}
                    >
                      {(!pickupLocId) && <option value="" disabled>Select pickup location…</option>}
                      {(locations || []).map(l => (
                        <option key={l.id} value={l.id}>
                          {(l.icon || '📍') + ' ' + l.name + ' — ' + fmtFee(l)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                      type="checkbox"
                      checked={diffReturn}
                      onChange={e => {
                        setDiffReturn(e.target.checked);
                        setReturnLocId(e.target.checked ? (returnLocId || pickupLocId) : '');
                      }}
                      style={{ accentColor: 'var(--ac)' }}
                    />
                    <label style={{ fontSize: 12, cursor: 'pointer' }} onClick={() => {
                      const next = !diffReturn;
                      setDiffReturn(next);
                      setReturnLocId(next ? (returnLocId || pickupLocId) : '');
                    }}>
                      Return at a different location
                    </label>
                  </div>

                  {diffReturn && (
                    <div className="fg">
                      <label className="fl">Return Location</label>
                      <select
                        className="fi"
                        value={returnLocId || ''}
                        onChange={e => setReturnLocId(e.target.value)}
                      >
                        <option value="" disabled>Select return location…</option>
                        {(locations || []).map(l => (
                          <option key={l.id} value={l.id}>
                            {(l.icon || '📍') + ' ' + l.name + ' — ' + fmtFee(l)}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </>
              );
            })()}

            <div className="sdiv" style={{ marginBottom: 2 }}>Pricing</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
              <div className="fg">
                <label className="fl">Pricing Type</label>
                <select className="fsel2" value={priceType} onChange={e => setPriceType(e.target.value)}>
                  <option value="day">Per day</option>
                  <option value="hour">Per hour</option>
                  <option value="enghour">Engine hours</option>
                  <option value="week">Per week</option>
                  <option value="fixed">Fixed price</option>
                </select>
              </div>
              <div className="fg">
                <label className="fl">Rate (€)</label>
                <input type="number" className="fi" value={rate} onChange={e => setRate(e.target.value)} />
              </div>
              <div className="fg">
                <label className="fl">Discount</label>
                <div style={{ display: 'flex', gap: 4 }}>
                  <input type="number" className="fi" value={discount} onChange={e => setDiscount(e.target.value)} />
                  <select className="fsel2" value={discType} onChange={e => setDiscType(e.target.value)} style={{ width: 60 }}>
                    <option value="eur">€</option>
                    <option value="pct">%</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Price breakdown */}
            <div style={{ background: 'var(--sur2)', border: '1px solid var(--bd)', borderRadius: 'var(--r)', padding: '10px 12px', fontSize: 11 }}>
              {/* Group pricing label */}
              {vehicle?.useGroupPricing && vehicle?.groupId && (() => {
                const g = (groups || []).find(gg => gg.id === vehicle.groupId);
                return g ? (
                  <div style={{ fontSize: 10, color: 'var(--bl)', fontWeight: 600, marginBottom: 6, padding: '3px 8px', background: 'var(--bl-l)', border: '1px solid var(--bl-b)', borderRadius: 5 }}>
                    📁 Group price: {g.icon} {g.name} — €{g.priceDay}/day
                  </div>
                ) : null;
              })()}
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                <span style={{ color: 'var(--tx3)' }}>{days} day{days !== 1 ? 's' : ''} × €{rate || 0}/{priceType}</span>
                <span>€{rawBase.toFixed(2)}</span>
              </div>
              {/* Rate rule adjustments */}
              {ruleDiscount > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3, color: 'var(--gr)' }}>
                  <span>📊 Rate rule discount ({applicableRules.filter(r => r.effect === 'discount').map(r => r.name).join(', ')})</span>
                  <span>-€{ruleDiscount.toFixed(2)}</span>
                </div>
              )}
              {ruleSurcharge > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3, color: 'var(--am)' }}>
                  <span>📊 Rate rule surcharge ({applicableRules.filter(r => r.effect === 'surcharge').map(r => r.name).join(', ')})</span>
                  <span>+€{ruleSurcharge.toFixed(2)}</span>
                </div>
              )}
              {disc > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3, color: 'var(--gr)' }}>
                  <span>Manual discount</span><span>-€{disc.toFixed(2)}</span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                <span style={{ color: 'var(--tx3)' }}>Subtotal</span>
                <span>€{(vatMode === 'inclusive' ? sub / (1 + vatRate) : sub).toFixed(2)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ color: 'var(--tx3)' }}>{`VAT (${Math.round(vatRate*100)}%)`}</span><span>€{vat.toFixed(2)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: 13, borderTop: '1px solid var(--bd)', paddingTop: 4 }}>
                <span>Total Agreed</span><span style={{ color: 'var(--br)' }}>€{total.toFixed(2)}</span>
              </div>
            </div>
          </div>
        )}

        {/* ── STEP 3: Photos & Condition ── */}
        {step === 3 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div className="sdiv" style={{ marginBottom: 2 }}>Pre-Rental Condition</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {COND_OPTIONS.map(opt => (
                <div key={opt.value} onClick={() => setCond(opt.value)} style={{
                  padding: '9px 12px',
                  border: `1.5px solid ${cond === opt.value ? 'var(--ac)' : 'var(--bd)'}`,
                  borderRadius: 'var(--r)', cursor: 'pointer', fontSize: 11,
                  background: cond === opt.value ? 'var(--ac-l)' : 'var(--sur)',
                }}>
                  {opt.label}
                </div>
              ))}
            </div>
            <div className="fg">
              <label className="fl">Condition Notes</label>
              <textarea className="fi" rows={2} value={condNote} onChange={e => setCondNote(e.target.value)} placeholder="Describe any existing marks or damage..." />
            </div>

            {/* Log pre-existing damage — when scratch/damage/major selected, let the
                operator file a damage report linked to this rental (documented as
                pre-existing at pickup). Also lists records already linked. */}
            {(() => {
              const linkedDmgs = (getDamagesForRental && contractId) ? (getDamagesForRental(contractId) || []) : [];
              const condTriggers = cond === 'small_scratch' || cond === 'damage' || cond === 'major_damage';
              if (!condTriggers && linkedDmgs.length === 0) return null;
              const presetSeverity = cond === 'major_damage' ? 'major' : cond === 'damage' ? 'moderate' : 'minor';
              const custNm = isNewCust ? `${newCust.fname} ${newCust.lname}`.trim() : custName;
              return (
                <div style={{ background: 'var(--ac-l)', border: '1px solid var(--ac-b)', borderRadius: 'var(--r)', padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ fontSize: 11, color: 'var(--ac)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                    <div>
                      {linkedDmgs.length > 0
                        ? <>📋 <strong>{linkedDmgs.length} damage record{linkedDmgs.length === 1 ? '' : 's'}</strong> logged for this rental</>
                        : <>📸 <strong>Log this damage</strong> as pre-existing so it's on file before handover</>}
                    </div>
                    <button
                      type="button" className="btn btn-primary btn-sm" style={{ whiteSpace: 'nowrap' }}
                      disabled={!selectedVid}
                      onClick={() => {
                        if (!selectedVid) { showToast('Select a vehicle first'); return; }
                        openModal('damage', {
                          vid: selectedVid, rentalId: contractId,
                          customerId: selectedCustomer?.id || null,
                          customerName: custNm || '',
                          description: condNote || '',
                          severity: presetSeverity,
                          paidBy: 'absorbed',     // pre-existing at pickup → not charged to the renter
                          status: 'reported',
                          foundAt: 'pickup',
                        });
                      }}
                    >
                      + Damage Report
                    </button>
                  </div>
                  {linkedDmgs.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {linkedDmgs.map(d => (
                        <button key={d.id} type="button" onClick={() => openModal('damage', d.id)}
                          style={{ background: 'var(--sur)', border: '1px solid var(--bd)', borderRadius: 6, padding: '6px 10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 11, cursor: 'pointer', textAlign: 'left' }}>
                          <span>{d.location || 'Damage'} — {d.description?.slice(0, 50)}{d.description?.length > 50 ? '…' : ''}</span>
                          <span style={{ fontFamily: "'DM Mono',monospace", fontWeight: 600 }}>€{(d.finalCost ?? d.estimatedCost ?? 0).toFixed(0)}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}

            <div className="sdiv" style={{ marginBottom: 2 }}>
              Photos
              <span style={{ fontSize: 10, fontWeight: 400, color: 'var(--tx3)', marginLeft: 8 }}>
                {Object.values(photos).filter(Boolean).length}/{PHOTO_SLOTS.length} added
              </span>
            </div>
            {/* Hidden file input shared by all slots */}
            <input ref={photoInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={onSlotFile} />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
              {PHOTO_SLOTS.map(label => (
                <div key={label} style={{
                  border: `2px ${photos[label] ? 'solid var(--gr-b)' : 'dashed var(--bd)'}`,
                  borderRadius: 'var(--r)', overflow: 'hidden',
                  background: photos[label] ? 'transparent' : 'var(--sur2)',
                }}>
                  {photos[label] ? (
                    <>
                      <img src={photos[label]} alt={label}
                        style={{ width: '100%', height: 88, objectFit: 'cover', display: 'block', cursor: 'pointer' }}
                        onClick={() => window.open(photos[label], '_blank')} />
                      <div style={{ padding: '4px 4px 5px', display: 'flex', gap: 3, justifyContent: 'center' }}>
                        <button type="button" className="btn btn-ghost btn-sm" style={{ padding: '2px 6px', fontSize: 9 }} onClick={() => openPhotoSlot(label)}>📁</button>
                        <button type="button" className="btn btn-ghost btn-sm" style={{ padding: '2px 6px', fontSize: 9 }} onClick={() => openSlotCamera(label)}>📷</button>
                        <button type="button" className="btn btn-ghost btn-sm" style={{ padding: '2px 6px', fontSize: 9, color: 'var(--ac)' }} onClick={() => removeSlotPhoto(label)}>✕</button>
                      </div>
                      <div style={{ textAlign: 'center', fontSize: 9, color: 'var(--gr)', paddingBottom: 4, fontWeight: 600 }}>✓ {label}</div>
                    </>
                  ) : (
                    <div style={{ padding: '18px 8px', textAlign: 'center', cursor: 'pointer', fontSize: 10, color: 'var(--tx3)' }}
                      onClick={() => openPhotoSlot(label)}>
                      <div style={{ fontSize: 22, marginBottom: 4 }}>📷</div>
                      <div style={{ fontWeight: 500, marginBottom: 2 }}>{label}</div>
                      <div style={{ fontSize: 9 }}>+ Add photo</div>
                      <button type="button" className="btn btn-ghost btn-sm" style={{ marginTop: 6, fontSize: 9, padding: '2px 8px' }}
                        onClick={e => { e.stopPropagation(); openSlotCamera(label); }}>📸 Camera</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── STEP 4: Agreement & Sign ── */}
        {step === 4 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div className="sdiv" style={{ marginBottom: 2 }}>Agreement Template</div>
            {(() => {
              const rentalTemplates = (agreementTemplates || []).filter(t => !t.appliesTo || t.appliesTo === 'rental' || t.appliesTo === 'both');
              if (rentalTemplates.length === 0) {
                return <div style={{ fontSize: 11, color: 'var(--tx3)', padding: '10px 12px', background: 'var(--sur2)', borderRadius: 'var(--r)' }}>No agreement templates available. Add one in <strong>Agreements</strong>.</div>;
              }
              return rentalTemplates.map(t => (
                <div key={t.id} onClick={() => setAgree(t.id)} style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '10px 12px',
                  border: `1.5px solid ${agree === t.id ? 'var(--ac)' : 'var(--bd)'}`,
                  borderRadius: 'var(--r)', cursor: 'pointer',
                  background: agree === t.id ? 'var(--ac-l)' : 'var(--sur)',
                }}>
                  <span style={{ fontSize: 20 }}>{t.icon || '📄'}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 11, fontWeight: 500 }}>{t.name}</div>
                    <div style={{ fontSize: 10, color: 'var(--tx3)' }}>{t.desc}</div>
                  </div>
                  {agree === t.id && <span style={{ color: 'var(--ac)', fontWeight: 700, fontSize: 13 }}>✓</span>}
                </div>
              ));
            })()}

            {/* Agreement — view the full rendered contract + print (incl. signature once signed) */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <div className="sdiv" style={{ marginBottom: 0 }}>Rental Agreement</div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => setShowFullAgreement(v => !v)}>
                    {showFullAgreement ? '▲ Collapse' : '📄 View'}
                  </button>
                  <button className="btn btn-ghost btn-sm" onClick={() => {
                    const { tmpl, ctx } = getRentalContract();
                    if (tmpl) printContract(tmpl, ctx, { title: `Contract ${contractId}` });
                    else showToast('No agreement template available');
                  }}>🖨️ Print</button>
                </div>
              </div>
              {showFullAgreement && (() => {
                const { tmpl, ctx } = getRentalContract();
                return (
                  <div style={{ maxHeight: 360, overflowY: 'auto', border: '1px solid var(--bd)', borderRadius: 'var(--r)', background: 'var(--sur2)', padding: 10 }}>
                    {tmpl
                      ? <ContractDocument template={tmpl} context={ctx} paperLook={true} />
                      : <div style={{ padding: 20, color: 'var(--tx3)', fontSize: 11 }}>No agreement template available. Add one in <strong>Agreements</strong>.</div>}
                  </div>
                );
              })()}
            </div>

            <div className="sdiv" style={{ marginBottom: 2 }}>Customer Signature</div>
            <div style={{ border: '1px solid var(--bd)', borderRadius: 'var(--r)', overflow: 'hidden' }}>
              <canvas
                ref={canvasRef}
                width={700}
                height={90}
                style={{ display: 'block', width: '100%', height: 90, cursor: 'crosshair', background: '#fafafa', touchAction: 'none' }}
                onMouseDown={canvasDown}
                onMouseMove={canvasMove}
                onMouseUp={canvasUp}
                onMouseLeave={canvasUp}
                onTouchStart={canvasDown}
                onTouchMove={canvasMove}
                onTouchEnd={canvasUp}
              />
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <button className="btn btn-ghost btn-sm" onClick={clearCanvas}>Clear</button>
              <button className="btn btn-ghost btn-sm" style={{ color: 'var(--gr)' }} onClick={confirmSignature}>✓ Confirm Signature</button>
              {signed && <span style={{ fontSize: 10, background: 'var(--gr)', color: '#fff', padding: '2px 9px', borderRadius: 10, fontWeight: 600 }}>Signed</span>}
            </div>

            <div className="fg">
              <label className="fl">Internal Notes</label>
              <textarea className="fi" rows={2} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Notes for your team..." />
            </div>

            {/* Mini summary */}
            {vehicle && (
              <div style={{ background: 'var(--gr-l)', border: '1px solid var(--gr-b)', borderRadius: 'var(--r)', padding: '10px 14px', fontSize: 11 }}>
                <div style={{ fontWeight: 600, color: 'var(--gr)', marginBottom: 5 }}>
                  ✅ Ready to create rental &nbsp;·&nbsp;
                  <span style={{ fontWeight: 400 }}>Contract: {contractId || nextContractId()}</span>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px 18px', color: 'var(--tx2)' }}>
                  <span>{vehicle.icon} {vehicle.name.split('–')[0].trim()}</span>
                  <span>{isNewCust ? `${newCust.fname} ${newCust.lname}`.trim() : custName}</span>
                  <span>{days} day{days > 1 ? 's' : ''} · €{total.toFixed(2)}</span>
                </div>
              </div>
            )}
          </div>
        )}

      </ModalBody>

      <ModalFooter>
        <span style={{ fontSize: 10, color: 'var(--tx3)', marginRight: 'auto' }}>Step {step} of {STEPS.length}</span>
        <button className="btn btn-ghost" onClick={() => step > 1 ? setStep(s => s - 1) : closeModal('add-rental')}>
          {step > 1 ? '← Back' : 'Cancel'}
        </button>
        {step < 4
          ? <button className="btn btn-primary" onClick={() => setStep(s => s + 1)}>Next →</button>
          : <>
              <button className="btn btn-ghost" onClick={() => submit(true)}>📅 Save as Booking</button>
              <button className="btn btn-primary" style={{ background: 'var(--gr)' }} onClick={() => submit(false)}>▶ Start Rental</button>
            </>
        }
      </ModalFooter>
    </Modal>

    {/* Camera overlay — rendered outside the modal so its z-index sits on top */}
    {camera && (
      <CameraCapture
        label={camera.label}
        onCapture={dataUrl => { camera.setPhoto(dataUrl); setCamera(null); }}
        onClose={() => setCamera(null)}
      />
    )}
    </>
  );
}
